import { useCallback, useRef } from 'react';
import useDebounceFunction from '../components/Hooks/useDebouncedFunction';

const DEBOUNCE_TIME = 1000;

interface UseStackProps<T> {
  getUniqueKey: (item: T) => string;
  onFinish?: (saved: T[]) => Promise<void>;
  isSaving?: boolean;
}

export const useStack = <T>({ onFinish, getUniqueKey }: UseStackProps<T>) => {
  const stackRef = useRef<T[]>([]);
  const { handleDebounceFunction } = useDebounceFunction({ debounceTime: DEBOUNCE_TIME });

  const persistStack = useCallback(async () => {
    const itemsToPersist = [...stackRef.current];
    if (itemsToPersist.length === 0) {
      return;
    }

    try {
      await onFinish?.(itemsToPersist);
    } catch (error) {
      throw error;
    } finally {
      stackRef.current = [];
    }
  }, [onFinish]);

  const pushItem = useCallback(
    (item: T) => {
      const key = getUniqueKey(item);
      const filtered = stackRef.current.filter((i) => getUniqueKey(i) !== key);
      stackRef.current = [item, ...filtered];

      handleDebounceFunction(persistStack);
    },
    [getUniqueKey, persistStack, handleDebounceFunction],
  );

  return {
    stackRef,
    pushItem,
  };
};
